import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Search,
  Plus,
  Upload,
  Filter,
  MoreHorizontal,
  Mail,
  Phone,
  Building,
  MapPin,
  Tag,
  Users,
  X,
  CheckSquare,
  Square,
  Send
} from 'lucide-react';

const LOAN_TYPES = ['DSCR', 'HELOC', 'Personal', 'Construction', 'Other'];
const SOURCES = ['Website', 'Referral', 'Ad', 'Cold Call', 'Event', 'Other'];

const ContactsPage = () => {
  const navigate = useNavigate();
  
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [referralBrokers, setReferralBrokers] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [loanTypeFilter, setLoanTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Selection
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  
  // Add Contact Form
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    property_address: '',
    loan_types: [],
    tags: [],
    source: '',
    referral_broker_id: ''
  });
  
  // Templates for bulk email
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    loadContacts();
    loadTemplates();
    loadReferralBrokers();
  }, [search, loanTypeFilter, sourceFilter]);

  const loadReferralBrokers = async () => {
    try {
      const res = await api.get('/referral-brokers?is_active=true');
      setReferralBrokers(res.data);
    } catch (error) {
      console.error('Failed to load referral brokers:', error);
    }
  };

  const loadContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (loanTypeFilter && loanTypeFilter !== 'all') params.append('loan_type', loanTypeFilter);
      if (sourceFilter && sourceFilter !== 'all') params.append('source', sourceFilter);
      params.append('do_not_contact', 'false');
      
      const [contactsRes, countRes] = await Promise.all([
        api.get(`/contacts?${params.toString()}`),
        api.get(`/contacts/count?${params.toString()}`)
      ]);
      
      setContacts(contactsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await api.get('/templates');
      setTemplates(res.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const contactData = {...newContact};
      if (!contactData.referral_broker_id) {
        delete contactData.referral_broker_id;
      }
      await api.post('/contacts', contactData);
      toast.success('Contact added successfully!');
      setShowAddDialog(false);
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
        property_address: '',
        loan_types: [],
        tags: [],
        source: '',
        referral_broker_id: ''
      });
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post('/contacts/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${res.data.imported} contacts. ${res.data.duplicates} duplicates skipped.`);
      setShowImportDialog(false);
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  const handleSelectContact = (contactId) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
    setSelectAllMatching(false);
  };

  const handleSelectAllOnPage = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set());
      setSelectAllMatching(false);
    } else {
      setSelectedContacts(new Set(contacts.map(c => c.contact_id)));
    }
  };

  const handleSelectAllMatching = () => {
    setSelectAllMatching(true);
    setSelectedContacts(new Set(contacts.map(c => c.contact_id)));
  };

  const handleBulkEmail = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    
    try {
      const filterCriteria = {};
      if (loanTypeFilter && loanTypeFilter !== 'all') filterCriteria.loan_type = loanTypeFilter;
      if (sourceFilter && sourceFilter !== 'all') filterCriteria.source = sourceFilter;
      
      const res = await api.post('/emails/bulk', {
        template_id: selectedTemplate,
        filter_criteria: filterCriteria,
        daily_cap: selectAllMatching ? totalCount : selectedContacts.size
      });
      
      toast.success(`Bulk email job queued! ${res.data.total_targets} emails will be sent.`);
      setShowBulkEmailDialog(false);
      setSelectedContacts(new Set());
      setSelectAllMatching(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue bulk emails');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success('Contact deleted');
      loadContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleToggleDoNotContact = async (contact) => {
    try {
      await api.put(`/contacts/${contact.contact_id}`, {
        do_not_contact: !contact.do_not_contact
      });
      toast.success(contact.do_not_contact ? 'Contact enabled for communications' : 'Contact marked as Do Not Contact');
      loadContacts();
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setLoanTypeFilter('all');
    setSourceFilter('all');
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="contacts-page">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold">Contacts</h1>
            <p className="text-muted-foreground mt-1">
              {totalCount} total contacts
            </p>
          </div>
          <div className="flex gap-3">
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-btn">
                  <Upload className="mr-2 h-4 w-4" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import Contacts</DialogTitle>
                  <DialogDescription>
                    Upload a CSV or XLSX file with your contacts.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-6">
                  <Input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleImport}
                    data-testid="import-file-input"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Required columns: first_name, last_name, email<br />
                    Optional: phone, company, property_address, loan_type, tags, source
                  </p>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-contact-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add New Contact</DialogTitle>
                  <DialogDescription>
                    Enter the contact details below.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddContact}>
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        value={newContact.first_name}
                        onChange={(e) => setNewContact({...newContact, first_name: e.target.value})}
                        required
                        data-testid="contact-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={newContact.last_name}
                        onChange={(e) => setNewContact({...newContact, last_name: e.target.value})}
                        required
                        data-testid="contact-last-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                        required
                        data-testid="contact-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        value={newContact.company}
                        onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                        data-testid="contact-company"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="source">Source</Label>
                      <Select
                        value={newContact.source}
                        onValueChange={(value) => setNewContact({...newContact, source: value})}
                      >
                        <SelectTrigger data-testid="contact-source">
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          {SOURCES.map(source => (
                            <SelectItem key={source} value={source}>{source}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="property_address">Property Address</Label>
                      <Input
                        id="property_address"
                        value={newContact.property_address}
                        onChange={(e) => setNewContact({...newContact, property_address: e.target.value})}
                        data-testid="contact-property-address"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Referral Broker</Label>
                      <Select
                        value={newContact.referral_broker_id}
                        onValueChange={(value) => setNewContact({...newContact, referral_broker_id: value})}
                      >
                        <SelectTrigger data-testid="contact-referral-broker">
                          <SelectValue placeholder="Select referral broker (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">None</SelectItem>
                          {referralBrokers.map(broker => (
                            <SelectItem key={broker.broker_id} value={broker.broker_id}>
                              {broker.name} ({broker.referral_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Loan Types Interested In</Label>
                      <div className="flex flex-wrap gap-2">
                        {LOAN_TYPES.map(type => (
                          <Badge
                            key={type}
                            variant={newContact.loan_types.includes(type) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => {
                              const types = newContact.loan_types.includes(type)
                                ? newContact.loan_types.filter(t => t !== type)
                                : [...newContact.loan_types, type];
                              setNewContact({...newContact, loan_types: types});
                            }}
                            data-testid={`loan-type-${type}`}
                          >
                            {type}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="save-contact-btn">Save Contact</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search & Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search contacts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={loanTypeFilter} onValueChange={setLoanTypeFilter}>
                <SelectTrigger className="w-[180px]" data-testid="loan-type-filter">
                  <SelectValue placeholder="Loan Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {LOAN_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-[180px]" data-testid="source-filter">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {SOURCES.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {(search || (loanTypeFilter && loanTypeFilter !== 'all') || (sourceFilter && sourceFilter !== 'all')) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {selectedContacts.size > 0 && (
          <Card className="mb-4 bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="font-medium">
                    {selectAllMatching ? totalCount : selectedContacts.size} contacts selected
                  </span>
                  {!selectAllMatching && selectedContacts.size === contacts.length && totalCount > contacts.length && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleSelectAllMatching}
                      className="text-primary"
                      data-testid="select-all-matching-btn"
                    >
                      Select all {totalCount} contacts matching this filter
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedContacts(new Set());
                      setSelectAllMatching(false);
                    }}
                  >
                    Clear Selection
                  </Button>
                  <Dialog open={showBulkEmailDialog} onOpenChange={setShowBulkEmailDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="bulk-email-btn">
                        <Send className="mr-2 h-4 w-4" />
                        Bulk Email
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Send Bulk Email</DialogTitle>
                        <DialogDescription>
                          Send emails to {selectAllMatching ? totalCount : selectedContacts.size} selected contacts
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Select Template</Label>
                          <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                            <SelectTrigger data-testid="bulk-template-select">
                              <SelectValue placeholder="Choose a template" />
                            </SelectTrigger>
                            <SelectContent>
                              {templates.map(template => (
                                <SelectItem key={template.template_id} value={template.template_id}>
                                  {template.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {templates.length === 0 && (
                          <p className="text-sm text-muted-foreground">
                            No templates found. <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/email')}>Create one first</Button>
                          </p>
                        )}
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkEmailDialog(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleBulkEmail} disabled={!selectedTemplate} data-testid="send-bulk-email-btn">
                          Send Emails
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts Table */}
        <Card data-testid="contacts-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedContacts.size === contacts.length && contacts.length > 0}
                      onCheckedChange={handleSelectAllOnPage}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Loan Types</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <div className="h-12 bg-muted animate-pulse rounded"></div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : contacts.length > 0 ? (
                  contacts.map((contact) => (
                    <TableRow
                      key={contact.contact_id}
                      className="table-row-hover cursor-pointer"
                      data-testid={`contact-row-${contact.contact_id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedContacts.has(contact.contact_id)}
                          onCheckedChange={() => handleSelectContact(contact.contact_id)}
                          data-testid={`select-contact-${contact.contact_id}`}
                        />
                      </TableCell>
                      <TableCell onClick={() => navigate(`/contacts/${contact.contact_id}`)}>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                              {contact.first_name?.[0]}{contact.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">
                              {contact.first_name} {contact.last_name}
                            </p>
                            {contact.company && (
                              <p className="text-xs text-muted-foreground">{contact.company}</p>
                            )}
                          </div>
                          {contact.do_not_contact && (
                            <Badge variant="destructive" className="text-xs">DNC</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/contacts/${contact.contact_id}`)}>
                        <span className="text-sm">{contact.email}</span>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/contacts/${contact.contact_id}`)}>
                        <span className="text-sm">{contact.phone || '-'}</span>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/contacts/${contact.contact_id}`)}>
                        <div className="flex flex-wrap gap-1">
                          {contact.loan_types?.slice(0, 2).map(type => (
                            <Badge key={type} variant="secondary" className="text-xs">
                              {type}
                            </Badge>
                          ))}
                          {contact.loan_types?.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{contact.loan_types.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/contacts/${contact.contact_id}`)}>
                        <span className="text-sm text-muted-foreground">{contact.source || '-'}</span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`contact-menu-${contact.contact_id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/contacts/${contact.contact_id}`)}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleDoNotContact(contact)}>
                              {contact.do_not_contact ? 'Enable Contact' : 'Mark Do Not Contact'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteContact(contact.contact_id)}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <Users className="h-12 w-12 mb-3 opacity-50" />
                        <p className="font-medium">No contacts found</p>
                        <p className="text-sm">Add your first contact to get started</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ContactsPage;
